import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { withRouter, useHistory } from 'react-router';
import _ from 'lodash';

import Modules from '~Components/Modules';
import Bong from '../POS';
import LoginModal from '../Tables/Components/LoginModal';
import BongMaster from '../Tables/Components/BongMaster';

import Onboarding from '~Scenes/Onboarding';
import { useSelector, useDispatch, useI18n, useAutoStaffLogout } from '~/Hooks';
import Journal from '~/Utils/journal';

import * as actions from '~actions';
import OrderIdManager from '~Components/OrderIdManager';
import { generateID } from '~reducers';
import { useStorage } from '~/Components/Storage';
import { useInventoryUniverse, useInventoryApi } from '~Components/InventoryUniverse';
import QuestionModal from '~ComponentsImproved/QuestionModal';
import DeviceStatusbar from '~ComponentsImproved/DeviceStatusBar';
import CartProvider, { useCart } from '~Components/CartProvider';
import useModal from '~ComponentsImproved/Modal/useModal';
import DemoNoPaymentsAllowed from '~Components/DemoHandler/NoPaymentsAllowed';
import { useCompetition } from '~Providers/CompetitionsProvider';
import QsrPayButtons from './Components/QSRPayButtons';
import StacksIcon from '~Assets/icons/stacks.svg';

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: var(--colorBlueGray1100);
  z-index: 1;
`;

interface Props {
  location: any;
  match: any;
}

const QSR = (props: Props) => {
  const cart = useCart();
  const dispatch = useDispatch();
  const history = useHistory();
  const [storage, updateStorage, storageLoading] = useStorage();
  const [paymentMethod, setPaymentMethod] = useState<{
    id: string;
    poiid?: string;
  }>();
  const bongPrintingHalted = useSelector((state) => state.BongPrintingHalted);
  const [showLogin, setShowLogin] = useState(false);
  let [, , venueId] = props.location.pathname.split('/');

  const demoPopup = useModal();
  const splitPaymentModal = useModal();

  /*
   * This will be set when unparking an order if the order already was printed on park
   * If true this will prevent another kitchen ticket to be printed
   */
  const [orderWasBonged, setOrderWasBonged] = useState<boolean>(false);

  const [bongReactKey, setBongReactKey] = useState(0);
  const [orderId, setOrderId] = useState<string>('');
  const competitions = useCompetition();

  // Tracks the category that was active when payment was initiated, so we can
  // restore it after reset when stayOnCategoryAfterPayment is enabled
  const [lastSelectedCategoryId, setLastSelectedCategoryId] = useState<string | undefined>(
    undefined,
  );

  const demoMode = useSelector((state) => state.demoMode);
  if (demoMode) {
    venueId = window.DEMO_MODE_VENUE_ID;
  }

  const [collectQuestionModal, setCollectQuestionModal] = useState<boolean>(false);

  const inventoryUniverse = useInventoryUniverse();
  const inventoryApi = useInventoryApi(venueId);
  const { i18n } = useI18n();

  const splitOrder = useSelector((state) => state.POSSplitOrder);

  const staffId = useSelector((state) => state.staff?.id);
  const autoStaffLogout = useAutoStaffLogout();

  // After we have started a payment process, this order will be populated
  const order = useSelector((state) => state.POSOrders.find((o) => o.id === orderId));

  // After the order has been created and the items have been added,
  // a payment is created and is returned here
  const payment = useSelector(
    (state) => order && state.POSPayments.find((p) => p.order === order.id),
  );

  const [enableQuantityRefetch, setEnableQuantityRefetch] = useState<boolean>(true);

  const itemsWithQuantity = useSelector((state) => state.POSItemsIds);

  const basketItemsWithQuantity: Array<{
    id: string;
    quantity: number;
  }> = [];

  cart.items.forEach((currentItem) => {
    const exist = basketItemsWithQuantity.find(
      (basketItem) => basketItem.id === currentItem.itemId,
    );
    if (!exist) {
      const currentItems = cart.items.filter((item) => item.itemId === currentItem.itemId);
      basketItemsWithQuantity.push({ id: currentItem.itemId, quantity: currentItems?.length });
    }
  });

  const leftItems: Array<{
    id: string;
    quantity: number;
    outOfStock: boolean;
  }> = [];

  itemsWithQuantity.forEach((currentItem) => {
    const basketItem = basketItemsWithQuantity.find(
      (basketItemWithQuantity) => basketItemWithQuantity.id === currentItem.id,
    );

    if (basketItem) {
      leftItems.push({
        ...currentItem,
        quantity: currentItem.quantity - basketItem.quantity,
      });
    } else {
      leftItems.push(currentItem);
    }
  });

  const handleEnableQuantityRefetch = (value: boolean) => {
    setEnableQuantityRefetch(value);
  };

  // On page boot, set the order ID and last used POS mode (single vs multi)
  const reloadOrderId = () => setOrderId(generateID());
  useEffect(() => {
    dispatch(actions.setLastUsedPosMode('SINGLE'));

    reloadOrderId();
    Journal.write('QSR POS Page Loaded');
  }, []);

  // Persist the POS mode so the tables view "Kassa" button still opens /pos after a reload.
  // Wait for storage to load: writing earlier would overwrite persisted settings with defaults.
  useEffect(() => {
    if (!storageLoading && storage.lastUsedPosMode !== 'SINGLE') {
      updateStorage({ lastUsedPosMode: 'SINGLE' });
    }
  }, [storageLoading]);

  /*
   * This function will change the key of the bong component
   * and cause it to reset its internal state. Used for new orders
   */
  function reset() {
    splitPaymentModal.hide();
    setBongReactKey(bongReactKey + 1);
    setOrderWasBonged(false);
    cart.setAppliedDiscount(null);
    cart.setItems([]);
    reloadOrderId();
    cart.updateServiceType(storage.serviceType);
    cart.clearUserData();

    if (!bongPrintingHalted) {
      cart.setEasyId(null);
    }
  }

  useEffect(() => {
    if (!bongPrintingHalted) {
      cart.setEasyId(null);
      reloadOrderId();
    }
  }, [bongPrintingHalted]);

  // When the order has been created, add items to the order and
  // create a payment right away
  useEffect(() => {
    if (!order || !paymentMethod?.id) {
      return;
    }

    const items = cart.packagedItems.filter((item) => !item.isMessage);

    items.forEach((item) => {
      const orderItem = {
        ...item,
        fraction: item.fraction ?? 1,
        count: item.count ?? 1,
        amount: item.amount ?? 0,
        entryId: item.entryId ?? '',
        discount: item.discount ?? 0,
      };
      dispatch(actions.addItemToOrder(order.id, orderItem));
    });

    if (!splitOrder) {
      const variant =
        paymentMethod.id === 'ADYEN'
          ? paymentMethod.poiid ?? storage.adyenTerminals[0]?.POIID
          : undefined;

      dispatch({
        type: 'POS_PAYMENT_REQUEST',
        method: paymentMethod.id,
        variant,
        canCancel: true,
        order: orderId,
        // Carries the acquired card through to the Adyen component, so it
        // charges the card the shopper already presented.
        meta: cart.cardIdentity ? { cardIdentity: cart.cardIdentity } : undefined,
      });
    }
  }, [order && order.id]);
  /*
   * Handle payment status updates
   */
  useEffect(() => {
    if (!payment) {
      return;
    }

    // Don't reset if splitting order payment
    if (splitOrder) {
      return;
    }

    if (payment.status === 'COMPLETED') {
      cart.setDesiredTime(null);

      // If we are collecting, check if the user wants to keep collecting
      if (bongPrintingHalted) {
        setCollectQuestionModal(true);
      }

      const items = _.groupBy(cart.packagedItems, (item) => item.itemId);

      if (staffId) {
        competitions.addSale({
          staffId: staffId,
          items: cart.packagedItems,
          tip: payment.meta.tip,
        });
      }

      Object.keys(items).forEach((key) => {
        const inInventory = inventoryUniverse.items.find((uniItem) => uniItem.itemId === key);
        if (!inInventory || !inInventory.count) return;

        const nrOfSameItem = items[key].length;
        inventoryApi.reduceStock(key, nrOfSameItem, 'Order');
      }, []);

      handleEnableQuantityRefetch(false);
      setTimeout(() => {
        dispatch(actions.setPOSItemsIds(leftItems));

        reset();

        // Setting: log the staff out once the payment has completed and the
        // order has been reset, so the next staff member has to log in.
        autoStaffLogout.logoutIfEnabled('payment');
      }, 1000);
    }

    if (payment.status === 'FAILED') {
      setTimeout(() => {
        dispatch({
          type: 'REMOVE_POS_PAYMENT',
          id: payment.id,
        });

        dispatch({
          type: 'REMOVE_POS_ORDER',
          id: orderId,
        });

        reloadOrderId();
      }, 1000);
    }
  }, [payment && payment.status]);

  const keepCollectingBongs = () => {
    setCollectQuestionModal(false);
  };

  const resumeBongPrinting = () => {
    setCollectQuestionModal(false);
    dispatch({ type: 'RESUME_BONG_PRINTING' });
  };

  return (
    <Wrapper>
      <DeviceStatusbar />

      <Bong
        paymentId={payment ? payment.id : null}
        key={bongReactKey}
        venueId={venueId}
        serviceType={cart.serviceType}
        orderId={orderId}
        reset={reset}
        initialCategoryId={storage.stayOnCategoryAfterPayment ? lastSelectedCategoryId : undefined}
        onCategoryChange={setLastSelectedCategoryId}
        buttons={
          <QsrPayButtons
            venueId={venueId}
            orderId={orderId}
            onComplete={reset}
            setPaymentMethod={(id: string, poiid?: string) => {
              setPaymentMethod({ id, poiid });
            }}
            orderWasBonged={orderWasBonged}
            isQsr
          />
        }
        onClose={() => history.push('gateway')}
        leftItems={leftItems}
        enableQuantityRefetch={enableQuantityRefetch}
        onEnableQuantityRefetch={handleEnableQuantityRefetch}
        setOrderWasBonged={setOrderWasBonged}
      />

      <Modules />

      <BongMaster />

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      <Onboarding />
      {orderId && (
        <OrderIdManager
          reset={() => {}}
          venueId={venueId}
          orderId={orderId}
          visible={false}
          orderEasyId={cart.easyId}
          setOrderEasyId={cart.setEasyId}
        />
      )}
      {!staffId && <LoginModal />}

      <QuestionModal
        icon={StacksIcon}
        question={i18n('POS.CollectQuestion')}
        isShowing={collectQuestionModal}
        onLeftClick={resumeBongPrinting}
        onRightClick={keepCollectingBongs}
      />
      <DemoNoPaymentsAllowed isShowing={demoPopup.isShowing} toggle={demoPopup.toggle} />
    </Wrapper>
  );
};

const Wrapped = withRouter(QSR);

export default () => (
  <CartProvider useSeparateTakeAwayMenu={true}>
    <Wrapped />
  </CartProvider>
);
